import React, { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { createTournament } from '../services/api'
import type { TournamentSettingsChild } from '../services/api'
import { Banner, Button, CompetitionsEditor } from '../components'
import type { CompetitionItem } from '../components'
import pageStyles from '../styles/page.module.scss'
import formStyles from '../styles/forms.module.scss'

const TournamentCreate: React.FC = () => {
    const navigate = useNavigate()

    // Parent name
    const [parentName, setParentName] = useState('')

    // Child competitions
    const [competitions, setCompetitions] = useState<CompetitionItem[]>([
        { draft: { name: '', shortName: '', type: '', gender: '', category: '' }, canDelete: false }
    ])

    // Edition settings
    const [createEdition, setCreateEdition] = useState(true)
    const [editionYear, setEditionYear] = useState(new Date().getFullYear())

    // State
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Validation
    const isValid = parentName.trim() && competitions.some(c => c.draft.name.trim())

    // Submit
    const handleSubmit = useCallback(async () => {
        if (!isValid) return

        setError(null)
        setSaving(true)

        try {
            const children: TournamentSettingsChild[] = competitions
                .filter(c => c.draft.name.trim())
                .map(c => ({
                    tc_name: c.draft.name.trim(),
                    short_name: c.draft.shortName.trim() || undefined,
                    tc_type: c.draft.type || undefined,
                    tc_gender: c.draft.gender || undefined,
                    category: c.draft.category || undefined
                }))

            const result = await createTournament({
                tc_name: parentName.trim(),
                children,
                create_edition: createEdition,
                edition_year: editionYear
            })

            // Redirect to the new tournament's detail page
            navigate(`/tournaments/${result.tc_competition_parent_id}`)
        } catch (err) {
            console.error('Failed to create tournament:', err)
            const message = err instanceof Error ? err.message : 'Unknown error'
            setError(`Failed to create tournament: ${message}`)
        } finally {
            setSaving(false)
        }
    }, [isValid, parentName, competitions, createEdition, editionYear, navigate])

    return (
        <div className={pageStyles.pageContainer}>
            {/* Header */}
            <div className={pageStyles.pageHeader}>
                <h1>Create Tournament</h1>
                <p>Define a new tournament with its child competitions and optionally create the first edition.</p>
            </div>

            {/* Error Banner */}
            {error && (
                <Banner
                    variant="error"
                    message={error}
                    onDismiss={() => setError(null)}
                    autoScroll
                />
            )}

            {/* Form */}
            <div className={formStyles.formContainer}>
                {/* Tournament Name */}
                <div className={formStyles.formSection}>
                    <h3>Tournament Name</h3>
                    <div className={formStyles.formField}>
                        <input
                            type="text"
                            value={parentName}
                            onChange={(e) => setParentName(e.target.value)}
                            placeholder="Enter tournament name (e.g., US Open)"
                            autoFocus
                        />
                    </div>
                </div>

                {/* Competitions */}
                <div className={formStyles.formSection}>
                    <CompetitionsEditor
                        competitions={competitions}
                        onChange={setCompetitions}
                        hint="Add the child competitions for this tournament (e.g., Men's Singles, Women's Doubles)."
                    />
                </div>

                {/* Edition Settings */}
                <div className={formStyles.formSection}>
                    <h3>First Edition</h3>
                    <div className={formStyles.formField}>
                        <label className={formStyles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={createEdition}
                                onChange={(e) => setCreateEdition(e.target.checked)}
                            />
                            Auto-create first edition (seasons) for this tournament
                        </label>
                        {createEdition && (
                            <div className={formStyles.inlineField}>
                                <label>Edition Year</label>
                                <input
                                    type="number"
                                    value={editionYear}
                                    onChange={(e) => setEditionYear(parseInt(e.target.value) || new Date().getFullYear())}
                                    min={2000}
                                    max={2100}
                                    className={formStyles.yearInput}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Submit */}
                <div className={formStyles.formFooter}>
                    <Button
                        variant="secondary"
                        onClick={() => navigate('/tournaments')}
                        disabled={saving}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!isValid || saving}
                    >
                        {saving ? 'Creating...' : 'Create Tournament'}
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default TournamentCreate
